import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Upload, Trash2, Image as ImageIcon, Sparkles, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Gallery() {
    const [designs, setDesigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiVibe, setAiVibe] = useState("");
    const [aiColors, setAiColors] = useState("");
    const [aiOut, setAiOut] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/designs");
            setDesigns(data);
        } catch (err) {
            console.error("gallery load failed:", err);
        }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const onPick = () => fileRef.current?.click();

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 6 * 1024 * 1024) {
            toast.error("Image must be under 6MB");
            return;
        }
        setUploading(true);
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                await api.post("/designs", { name: file.name.replace(/\.[^.]+$/, ""), image_b64: reader.result });
                toast.success("Design uploaded for the AI printer ✨");
                fileRef.current.value = "";
                await load();
            } catch (err) {
                console.error("design upload failed:", err);
                toast.error("Upload failed");
            }
            setUploading(false);
        };
        reader.readAsDataURL(file);
    };

    const onDelete = async (id) => {
        try {
            await api.delete(`/designs/${id}`);
            setDesigns((d) => d.filter((x) => x.design_id !== id));
            toast.success("Design removed");
        } catch (err) {
            console.error("design delete failed:", err);
            toast.error("Delete failed");
        }
    };

    const generateDescription = async () => {
        if (!aiVibe.trim()) {
            toast.error("Tell me the vibe first");
            return;
        }
        setAiBusy(true);
        try {
            const { data } = await api.post("/ai/design-description", { vibe: aiVibe, colors: aiColors });
            setAiOut(data.description || "");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI unavailable");
        }
        setAiBusy(false);
    };

    const copyDesc = () => {
        navigator.clipboard.writeText(aiOut);
        toast.success("Copied");
    };

    return (
        <div className="max-w-[1200px] mx-auto space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-rose">Your gallery</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-2">Past designs &amp; AI uploads</h1>
                    <p className="text-white/60 mt-2 max-w-xl">
                        Upload any image — kid&apos;s photo, custom art, screenshot — and we&apos;ll print it onto your nails at 3,400 DPI.
                    </p>
                </div>
                <button
                    data-testid="gallery-upload-btn"
                    onClick={onPick}
                    disabled={uploading}
                    className="px-6 py-3 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium flex items-center gap-2 disabled:opacity-50"
                >
                    <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload design"}
                </button>
                <input
                    type="file"
                    accept="image/*"
                    ref={fileRef}
                    onChange={onUpload}
                    className="hidden"
                    data-testid="gallery-upload-input"
                />
            </div>

            {/* AI Description */}
            <div className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/10 to-rose/10 overflow-hidden">
                <button
                    type="button"
                    onClick={() => setAiOpen(!aiOpen)}
                    data-testid="gallery-ai-toggle"
                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.03] transition"
                >
                    <div className="flex items-center gap-3">
                        <Sparkles className="w-4 h-4 text-gold" />
                        <div className="text-left">
                            <p className="font-serif text-lg">Describe your vibe with AI</p>
                            <p className="text-xs text-white/55">Get a luxe nail-art description for your Instagram post or design consult</p>
                        </div>
                    </div>
                    <span className="text-xs tracking-widest text-gold uppercase">{aiOpen ? "Close" : "Open"}</span>
                </button>
                {aiOpen && (
                    <div className="px-6 pb-6 grid gap-3">
                        <input
                            value={aiVibe}
                            onChange={(e) => setAiVibe(e.target.value)}
                            placeholder="Vibe (e.g. 'bridal ethereal', 'vegas showgirl', 'quiet luxury')"
                            data-testid="ai-vibe-input"
                            className="bg-ink/40 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 outline-none"
                        />
                        <input
                            value={aiColors}
                            onChange={(e) => setAiColors(e.target.value)}
                            placeholder="Colors (optional — e.g. 'nude + rose gold + ivory')"
                            data-testid="ai-colors-input"
                            className="bg-ink/40 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 outline-none"
                        />
                        <button
                            type="button"
                            onClick={generateDescription}
                            disabled={aiBusy || !aiVibe.trim()}
                            data-testid="ai-generate-btn"
                            className="self-start px-6 py-2.5 rounded-full bg-gold text-ink font-medium text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
                        >
                            {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            Generate description
                        </button>
                        {aiOut && (
                            <div
                                data-testid="ai-output"
                                className="mt-2 p-5 rounded-2xl border border-gold/30 bg-ink/30 relative"
                            >
                                <button
                                    type="button"
                                    onClick={copyDesc}
                                    title="Copy"
                                    className="absolute top-3 right-3 p-2 rounded-full text-white/55 hover:text-gold hover:bg-white/5 transition"
                                >
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                                <p className="font-serif text-base leading-relaxed pr-8">{aiOut}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={`gallery-skel-${i}`} className="aspect-square rounded-2xl bg-surface animate-pulse" />
                    ))}
                </div>
            ) : designs.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-white/15 p-16 text-center">
                    <ImageIcon className="w-10 h-10 mx-auto text-rose/60" />
                    <p className="mt-4 font-serif text-2xl">Nothing here yet</p>
                    <p className="mt-2 text-white/50">Upload a photo above and your AI nail art is one tap away.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {designs.map((d) => (
                        <div key={d.design_id} data-testid={`design-${d.design_id}`} className="group relative aspect-square rounded-2xl overflow-hidden border border-white/10 bg-surface">
                            <img src={d.image_b64} alt={d.name} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 to-transparent opacity-0 group-hover:opacity-100 transition flex flex-col justify-end p-3">
                                <p className="font-serif text-sm truncate">{d.name}</p>
                                <button
                                    onClick={() => onDelete(d.design_id)}
                                    data-testid={`design-delete-${d.design_id}`}
                                    className="mt-2 self-end text-xs text-rose hover:underline flex items-center gap-1"
                                >
                                    <Trash2 className="w-3 h-3" /> Remove
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
